import URI from 'urijs';

const MAX_RETRIES = 3;
const RETRY_BASE_DELAY_MS = 1000;

/**
 * Fetches raw enrichment records for the given IMS IDs from the bulk endpoint.
 * Retries up to MAX_RETRIES times with exponential backoff (0 ms, 1 s, 2 s).
 *
 * GET /api/irrigation-monitoring-sites/enrichment?ids[]=1&ids[]=2
 * → [{ id, site_name, latitude, longitude, application_rate }, ...]
 *
 * @param {number[]} ids
 * @param {string} bearerToken
 * @param {string} selectedDatabase
 * @returns {Promise<Array>}
 */
async function fetchEnrichmentRaw(ids, bearerToken, selectedDatabase) {
  const url = new URI(
    `${process.env.REACT_APP_PROPERTIES_API_URL}api/irrigation-monitoring-sites/enrichment`
  );
  url.setQuery('ids[]', ids);

  let lastError;
  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    if (attempt > 0) {
      await new Promise((resolve) =>
        setTimeout(resolve, RETRY_BASE_DELAY_MS * 2 ** (attempt - 1))
      );
    }
    try {
      const res = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${bearerToken}`,
          SeasonalDatabase: selectedDatabase
        }
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      return await res.json();
    } catch (err) {
      lastError = err;
    }
  }
  throw lastError;
}

/**
 * Enriches an array of IMS records by fetching site_name, latitude, longitude,
 * and application_rate from the bulk enrichment endpoint, then merging those
 * fields into each record.
 *
 * Returns a new array — the original objects are not mutated.
 * If the array is empty, returns it unchanged without making a network request.
 *
 * @param {object[]} sites - IMS records as returned by GET /api/properties
 * @param {string} bearerToken
 * @param {string} selectedDatabase
 * @returns {Promise<object[]>}
 */
export async function enrichSites(sites, bearerToken, selectedDatabase) {
  if (!sites.length) return sites;
  const ids = sites.map((s) => s.id);
  const enrichments = await fetchEnrichmentRaw(ids, bearerToken, selectedDatabase);
  const byId = Object.fromEntries(enrichments.map((e) => [e.id, e]));
  return sites.map((site) => ({ ...site, ...(byId[site.id] ?? {}) }));
}

/**
 * Enriches a single IMS record.
 *
 * @param {object} site
 * @param {string} bearerToken
 * @param {string} selectedDatabase
 * @returns {Promise<object>}
 */
export async function enrichSite(site, bearerToken, selectedDatabase) {
  const results = await enrichSites([site], bearerToken, selectedDatabase);
  return results[0];
}
